import { HostEvents } from './HostEvents';
import { sanitizeJsonString } from './jsonUtils';

/**
 * @class 设备上报
 * @description 统一管理来自上位机（WebSocket或Qt）的回调事件。
 * 它作为 window 全局回调的唯一代理，并将事件分发给所有订阅者。
 */
class DeviceCallbackManager {
  constructor() {
    /**
     * @private
     * @type {Object.<string, Array<Function>>}
     */
    this.subscriptions = {};
    this.initialize();
  }

  /**
   * @private
   * 初始化管理器，为所有在 HostEvents.js 中定义的事件创建 window 代理。
   * 这确保了无论何时（即使在组件订阅之前）从宿主调用 window.eventName，都不会导致错误。
   */
  initialize() {
    HostEvents.all.forEach(eventName => {
      // 为每个事件初始化一个空的订阅者数组
      this.subscriptions[eventName] = [];

      // 在 window 对象上创建代理函数
      window[eventName] = (payload) => {
        console.log(`[设备上报] Event received: ${eventName}`);
        
        // 如果是字符串，进行清洗处理
        const cleanedPayload = typeof payload === 'string' ? sanitizeJsonString(payload) : payload;
        
        const subscribers = this.subscriptions[eventName];

        if (subscribers && subscribers.length > 0) {
          // 将清洗后的数据分发给所有订阅者
          subscribers.forEach(callback => {
            try {
              callback(cleanedPayload);
            } catch (e) {
              console.error(`[设备上报] Error in callback for event "${eventName}":`, e);
            }
          });
        }
      };
    });
  }

  /**
   * 订阅一个来自宿主的事件。
   * @param {string} eventName - 事件名称，必须是 HostEvents 中定义的。
   * @param {Function} callback - 当事件触发时执行的回调函数。
   */
  subscribe(eventName, callback) {
    if (!this.subscriptions[eventName]) {
      console.warn(`[设备上报] Subscribing to an unknown event: "${eventName}". Make sure it is defined in HostEvents.js.`);
      this.subscriptions[eventName] = [];
    }
    if (!this.subscriptions[eventName].includes(callback)) {
      this.subscriptions[eventName].push(callback);
    }
  }

  /**
   * 取消订阅一个事件。
   * @param {string} eventName - 事件名称。
   * @param {Function} callback - 需要移除的回调函数。
   */
  unsubscribe(eventName, callback) {
    const subscribers = this.subscriptions[eventName];
    if (subscribers) {
      const index = subscribers.indexOf(callback);
      if (index > -1) {
        subscribers.splice(index, 1);
      }
    }
  }
}

// 导出单例
export const deviceCallbackManager = new DeviceCallbackManager();
